import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import { CoinInfoApi } from "src/DAL/Profile/Profile";
import { convertToTitleCase } from "src/utils/convertHtml";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrCard from "src/design-system/components/GrCard";
import GrDataTable from "src/design-system/components/GrDataTable";
import GrTablePagination from "src/design-system/components/GrTablePagination";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrIcon from "src/design-system/components/GrIcon";
import GrFilterButton from "src/design-system/components/GrFilterButton";
import GrActiveFilters from "src/design-system/components/GrActiveFilters";
import CoinsFilter, { TYPE_OPTIONS } from "./CoinsFilter";

// Coins History (/dashboard-coins-info): the coins the member earned or lost, with a Type filter.
const ROWS_PER_PAGE_OPTIONS = [50, 100, 150];

const EMPTY_FILTER = {
  status: "",
  type: "",
};

const loaderSx = {
  minHeight: `calc(${gr.space[12]} * 5)`,
  alignItems: "center",
};

// Member names for the API's coin types (food and workout have their own names).
const coinTypeLabel = (type) =>
  type === "food"
    ? "Nutrition"
    : type === "workout"
      ? "Fitness"
      : convertToTitleCase(type);

const columns = [
  {
    key: "points_type",
    label: "Type",
    primary: true,
    render: (row) => coinTypeLabel(row.points_type),
  },
  {
    key: "points_earned",
    label: "Coins",
    render: (row) => row.points_earned,
  },
  {
    key: "history_type",
    label: "Earned/Deducted",
    render: (row) => (
      <GrStatusPill status={row.history_type === "earned" ? "ok" : "overdue"}>
        {convertToTitleCase(row.history_type)}
      </GrStatusPill>
    ),
  },
  {
    key: "createdAt",
    label: "Date",
    render: (row) =>
      row.createdAt ? moment(row.createdAt).format("D MMM YYYY") : "N/A",
  },
];

export default function RecentPointsTable() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  // The API pages on the server: userList is this page's rows only.
  const [userList, setUserList] = useState([]);
  // Loading from the first frame (the list loads on mount), so "No results found" never flashes first.
  const [isLoading, setIsLoading] = useState(true);
  const [filterDrawerState, setFilterDrawerState] = useState(false);
  // filterState = what the drawer shows; filterStateUpdated = what the list is loaded with.
  const [filterState, setFilterState] = useState(EMPTY_FILTER);
  const [filterStateUpdated, setFilterStateUpdated] = useState(EMPTY_FILTER);

  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
    setFilterState(filterStateUpdated);
  };

  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  // Applying a filter starts again at page 0. A new object each time, so the list always
  // reloads (also when the same filter is applied again), with one request.
  const applyFilter = (data) => {
    setFilterStateUpdated({ ...data });
    setPage(0);
    handleCloseFilterDrawer();
  };

  const searchFunction = () => {
    applyFilter(filterState);
  };

  const handleClearFilter = () => {
    setFilterState(EMPTY_FILTER);
    applyFilter(EMPTY_FILTER);
    localStorage.removeItem("WorkoutListSearch");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Every page, rows-per-page or filter change reloads the list with the applied filter.
  useEffect(() => {
    // Only the latest request may fill the list (a slow older answer must not overwrite it).
    let isCurrent = true;
    const getcoinListing = async () => {
      setIsLoading(true);
      const result = await CoinInfoApi(page, rowsPerPage, filterStateUpdated?.type);
      if (!isCurrent) return;
      if (result.code === 200) {
        setUserList(result?.reward_points || []);
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setUserList([]);
      }
      setIsLoading(false);
    };
    getcoinListing();
    return () => {
      isCurrent = false;
    };
  }, [page, rowsPerPage, filterStateUpdated, enqueueSnackbar]);

  // Filters in use on the list (shown on the Filters button); the drawer only sets the type.
  const activeFilterCount = filterStateUpdated?.type ? 1 : 0;
  // The chip shows the type as the drawer names it; × = Clear All (the only filter).
  const appliedType = TYPE_OPTIONS.find((o) => o.value === filterStateUpdated?.type);
  const activeFilters = appliedType
    ? [{ key: "type", label: appliedType.label, onClear: handleClearFilter }]
    : [];

  // The API's total_pages leaves out a last, part-full page (stage: 3 pages of 50 + 37 more on page 4),
  // so the pager goes by the rows instead: a full page may have more after it (-1 = total not known,
  // "of more than 50", Next stays on); a part-full page is the last one.
  const totalCount =
    userList.length === rowsPerPage ? -1 : page * rowsPerPage + userList.length;

  return (
    <GrPage sx={{ pb: gr.space[8] }}>
      <GrPageHeader
        title="Coins History"
        onBack={() => navigate(-1)}
        actions={
          <GrFilterButton onClick={handleOpenFilterDrawer} count={activeFilterCount}>
            Filters
          </GrFilterButton>
        }
      />
      <GrActiveFilters
        filters={activeFilters}
        onOpen={handleOpenFilterDrawer}
        sx={{ mb: gr.space[4] }}
      />
      <GrCard as="div">
        {isLoading ? (
          // While loading: the headings and the rows' space, so the card does not pop in and jump.
          <>
            <GrDataTable ariaLabel="Coins History" columns={columns} rows={[]} />
            <GrLoader sx={loaderSx} />
          </>
        ) : userList.length ? (
          <GrDataTable
            ariaLabel="Coins History"
            columns={columns}
            rows={userList}
            getRowKey={(row, index) => row._id || index}
          />
        ) : (
          // Nothing to show: the headings stay, so the member still sees what the list holds.
          <>
            <GrDataTable ariaLabel="Coins History" columns={columns} rows={[]} />
            <GrEmptyState
              compact
              icon={<GrIcon icon={icons.search} />}
              title="No results found"
            />
          </>
        )}
        <GrTablePagination
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </GrCard>
      <CoinsFilter
        open={filterDrawerState}
        onClose={handleCloseFilterDrawer}
        filterData={filterState}
        setFilterData={setFilterState}
        searchSubmitFilter={searchFunction}
        handleClearFilter={handleClearFilter}
      />
    </GrPage>
  );
}
